import React, { useEffect, useState } from "react";
import styled from "styled-components";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import { useAxiosConfigGlobal } from "../../Config/axios";
import { distributionUrl } from "../../Config/url";
import { ImgController } from "../../Scripts/ImgController";
import Pagination from "../../UI/pagination";

const Wrapper = styled.div`
    .table-border { border: 1px solid #e5e7eb; }
    .table-header { background: #f3f4f6; }
    tr { border-bottom: 1px solid #e5e7eb; }
    tr:last-child { border-bottom: none; }
    .list td { padding: 18px 24px; }

    .active-tab-container {
        position: relative;
        &::after {
            content: "";
            position: absolute;
            bottom: -2px;
            left: 0;
            width: 100%;
            height: 3px;
            background: #0061ff;
            border-radius: 10px;
        }
    }

    .custom-tab-button {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        padding: 16px 20px;
        gap: 12px;
        background: #fff;
        border: 1px solid #f0f0f0;
        border-radius: 8px;
        width: 100%;
        transition: all 0.2s ease;
        cursor: pointer;
        &:hover { background: #f9fafb; }
    }

    .active-tab-button {
        background: #f0f7ff;
    }

    @media (max-width: 767px) {
        .list td { padding: 12px; }
        .custom-tab-button {
            flex-direction: column;
            padding: 12px;
            gap: 4px;
        }
    }
`;

const Creation = ({ oneRingRulesAll }) => {
    const axiosConfig = useAxiosConfigGlobal();
    const [activeTab, setActiveTab] = useState("albums");
    const [listData, setListData] = useState([]);
    const [listMeta, setListMeta] = useState({});
    const [listLoaded, setListLoaded] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [paginationLinks, setPaginationLinks] = useState([]);
    const [counts, setCounts] = useState({ albums: 0, royalties: 0 });

    const getStatusLabel = (status) => {
        switch (status) {
            case 'pending': return <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-yellow-100 text-yellow-600">Хүлээгдэж буй</span>;
            case 'paid': return <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-green-100 text-green-600">Олгогдсон</span>;
            case 'reversed': return <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-red-100 text-red-600">Буцаагдсан</span>;
            default: return <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-gray-100 text-gray-400">{status}</span>;
        }
    };

    // 1. Тоог татах хэсэг (res.data.data.meta гэж зассан)
    useEffect(() => {
        document.title = "MMusic - Уран бүтээл";
        const fetchCounts = async () => {
            try {
                const [albumRes, royaltyRes] = await Promise.all([
                    axios.get(`${distributionUrl}panel-api/oauth/artist:albums`, axiosConfig),
                    axios.get(`${distributionUrl}panel-api/oauth/royalties`, axiosConfig)
                ]);

                setCounts({
                    albums: albumRes.data.meta?.total || 0,
                    royalties: royaltyRes.data.meta?.total || 0
                });
            } catch (err) {
                console.error("Counts fetch error:", err);
                if (err.response?.status === 401) oneRingRulesAll("logout");
            }
        };
        fetchCounts();
    }, []);

    // 2. Жагсаалт татах хэсэг (res.data.data.data гэж зассан)
    useEffect(() => {
        setListLoaded(false);
        const path = activeTab === "albums"
            ? `panel-api/oauth/artist:albums?page=${pageNumber}`
            : `panel-api/oauth/royalties?page[number]=${pageNumber}`;

        axios.get(distributionUrl + path, axiosConfig)
            .then((res) => {
                // ТАНЫ ЗУРАГ ДЭЭРХ БҮТЭЦ: res.data -> data -> data (жагсаалт)
                const mainResponse = res.data;

                if (mainResponse) {
                    setListData(mainResponse.data || []);
                    setListMeta(mainResponse.meta || {});
                    setPaginationLinks(mainResponse.meta?.links || []);
                }
                setListLoaded(true);
            })
            .catch((err) => {
                console.error("List fetch error:", err);
                setListLoaded(true);
                if (err.response?.status === 401) oneRingRulesAll("logout");
            });
    }, [activeTab, pageNumber]);

    const renderTableHeader = () => {
        let headers = activeTab === "albums"
            ? ["Цомог", "Төрөл", "Дууны тоо", "Үнэ", "Төлөв"]
            : ["Бүтээгдэхүүн", "Үндсэн үнэ", "Шимтгэл", "Цэвэр дүн", "Миний орлого", "Төлөв", "Огноо"];

        return (
            <tr className="table-header text-left">
                {headers.map((h, i) => (
                    <th key={i} className="px-6 py-4 text-[11px] font-bold text-gray-500 uppercase tracking-widest">{h}</th>
                ))}
            </tr>
        );
    };

    const renderTableBody = () => {
        if (!listLoaded) return [1, 2, 3].map(a => <tr key={a}><td colSpan="7" className="p-6"><Skeleton height={40}/></td></tr>);
        if (listData.length === 0) return <tr><td colSpan="7" className="p-10 text-center text-gray-400">Мэдээлэл олдсонгүй</td></tr>;

        return listData.map((item) => (
            <tr key={item.id} className="list hover:bg-gray-50 transition-colors">
                {activeTab === "albums" ? (
                    <>
                        <td>
                            <div className="flex items-center gap-3">
                                <ImgController src={item.cover} className="w-10 h-10 rounded object-cover shadow-sm" />
                                <div className="text-sm font-bold text-gray-800">{item.title}</div>
                            </div>
                        </td>
                        <td className="text-[11px] font-bold uppercase text-blue-500">{item.release_type}</td>
                        <td className="text-sm text-gray-600 font-medium">{item.songs_count} дуу</td>
                        <td className="text-sm font-bold text-gray-700">
                            {item.is_saleable ? `${(item.pricing || 0).toLocaleString()} ₮` : "-"}
                        </td>
                        <td>
                            <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${item.is_published ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                                {item.is_published ? "Нийтлэгдсэн" : "Ноорог"}
                            </span>
                        </td>
                    </>
                ) : (
                    <>
                        <td>
                            <div className="text-sm font-bold text-gray-800">{item.description}</div>
                            <div className="text-[10px] text-gray-400 uppercase tracking-tighter">ID: {item.model_id} | {item.invoice_number}</div>
                        </td>
                        {/* ЭНД БАЙСАН item.currency_code-УУДЫГ УСТГАЖ, Шууд ₮ БОЛГОЛОО */}
                        <td className="text-sm font-medium text-gray-600">{(item.sale_amount || 0).toLocaleString()} ₮</td>
                        <td className="text-sm font-medium text-red-400">- {(item.total_fee || 0).toLocaleString()} ₮</td>
                        <td className="text-sm font-medium text-gray-800">{(item.net_amount || 0).toLocaleString()} ₮</td>
                        <td className="text-sm font-bold text-blue-600">{(item.royalty_amount || 0).toLocaleString()} ₮</td>
                        <td>{getStatusLabel(item.status)}</td>
                        <td className="text-xs text-gray-500 font-medium">{item.purchased_at}</td>
                    </>
                )}
            </tr>
        ));
    };

    return (
        <Wrapper className="py-4 px-4 md:py-8 md:px-5">
            <h5 className="text-mmusic text-xl font-bold pb-4 md:pb-6 md:pl-2 tracking-tight">Миний уран бүтээл</h5>

            <div className="grid grid-cols-2 gap-3 md:gap-4 mb-6 md:mb-8 w-full">
                <div className={`${activeTab === "albums" ? "active-tab-container" : ""} w-full`} onClick={() => { setActiveTab("albums"); setPageNumber(1); }}>
                    <div className={`custom-tab-button shadow-sm ${activeTab === "albums" ? "active-tab-button" : ""}`}>
                        <span className="text-xl md:text-2xl font-black text-gray-800">{counts.albums}</span>
                        <span className="text-[10px] md:text-xs font-bold text-gray-500 uppercase tracking-wider md:tracking-widest text-center">Дижитал цомог</span>
                    </div>
                </div>
                <div className={`${activeTab === "royalties" ? "active-tab-container" : ""} w-full`} onClick={() => { setActiveTab("royalties"); setPageNumber(1); }}>
                    <div className={`custom-tab-button shadow-sm ${activeTab === "royalties" ? "active-tab-button" : ""}`}>
                        <span className="text-xl md:text-2xl font-black text-gray-800">{counts.royalties}</span>
                        <span className="text-[10px] md:text-xs font-bold text-gray-500 uppercase tracking-wider md:tracking-widest text-center">Борлуулалт</span>
                    </div>
                </div>
            </div>

            <div className="table-border rounded-xl bg-white shadow-sm overflow-hidden">
                <div className="px-4 md:px-6 py-4 md:py-5 border-b border-gray-100">
                    <h4 className="font-bold text-gray-800 text-xs uppercase tracking-widest">
                        {activeTab === "albums" ? "Миний цомгууд" : "Цомог борлуулалтын түүх"}
                    </h4>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px]">
                        <thead>{renderTableHeader()}</thead>
                        <tbody>{renderTableBody()}</tbody>
                    </table>
                </div>
                <div className="p-4 md:p-6 border-t flex flex-col md:flex-row justify-between items-center gap-4">
                    <h5 className="text-[11px] font-bold text-gray-400 uppercase tracking-tighter">
                        Нийт {listMeta?.total || 0} илэрцээс {listMeta?.from || 0} - {listMeta?.to || 0}
                    </h5>
                    <Pagination
                        paginationLinks={paginationLinks}
                        listMeta={listMeta}
                        hostUrl={distributionUrl}
                        setRequestParams={(trimmedPath) => {
                            if (!trimmedPath) return;
                            const url = new URL(trimmedPath, "http://dummy.com");
                            const p = url.searchParams.get("page") || url.searchParams.get("page[number]");
                            if (p) setPageNumber(Number(p));
                        }}
                    />
                </div>
            </div>
        </Wrapper>
    );
};

export default Creation;